import { useNavigate } from 'react-router-dom';
import { MAX_BRANDS_PER_USER } from '@affily/shared/constants';
import { useFetch } from '../../hooks/useFetch.js';
import { useBrand } from '../../hooks/useBrand.jsx';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { BrandPage } from '../../components/BrandPage.jsx';
import { Alert, Button, Spinner } from '../../components/ui.jsx';

/**
 * The current brand's page, open for editing — the same page as /brands/:slug,
 * so what the owner fills in is what creators see.
 *
 * `createNew` is "+ Add a brand": an empty page that creates another brand on
 * this account, rather than editing the current one. With no brand yet, the
 * page creates the first.
 */
export default function BrandProfileEditor({ createNew = false }) {
  const navigate = useNavigate();
  const brands = useBrand();
  const slug = createNew ? null : brands.activeBrand?.slug;
  const { data, error, loading, reload } = useFetch(slug ? `/brands/${slug}` : null, {
    skip: !slug,
  });

  const existing = slug ? (data?.brand ?? null) : null;

  if (!brands.loaded || (slug && loading && !data)) {
    return (
      <DashboardLayout side="brand">
        <Spinner />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      side="brand"
      title={existing ? 'Brand page' : createNew ? 'Add a brand' : 'Set up your brand'}
      description={
        existing
          ? 'Your public page, open for editing. Preview shows it exactly as creators will.'
          : createNew
            ? 'Another brand on this account, with its own programs, partners and billing.'
            : 'Fill in your page, then you can list a program.'
      }
      actions={
        existing && (
          <div className="flex flex-wrap gap-2">
            {/* A draft's page is a 404 to visitors, so there is nothing to view yet. */}
            {existing.isPublished && (
              <Button to={`/brands/${existing.slug}`} variant="outline" size="sm">
                View public page
              </Button>
            )}
            {brands.brands.length < MAX_BRANDS_PER_USER && (
              <Button to="/dashboard/brand/new" variant="outline" size="sm">
                + Add another brand
              </Button>
            )}
          </div>
        )
      }
    >
      {error ? (
        <Alert>{error.message}</Alert>
      ) : (
        <BrandPage
          // The profile and "Add a brand" share this route's component; a new
          // brand gets a fresh page, not the last one's edits and mode.
          key={existing?.id ?? 'new'}
          brand={existing}
          programs={data?.programs ?? []}
          canEdit
          startEditing
          onCancelNew={() => navigate(-1)}
          onSaved={async (saved) => {
            if (existing) {
              await Promise.all([reload(), brands.reload()]);
            } else {
              // Switch to the new brand before moving on, so its first
              // program is created under it.
              await brands.reload(saved?.id);
              navigate('/dashboard/brand/programs/new');
            }
          }}
        />
      )}
    </DashboardLayout>
  );
}
